(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
  const cache = new Map();
  let pickerState = null;
  let pickerReturn = null;

  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = window.zaT(text);
    return node;
  };

  const normalizeImage = (url) => {
    if (!url) return '';
    const value = String(url);
    if (value.startsWith('//')) return `${window.location.protocol}${value}`;
    return value;
  };

  const productJsonUrl = (url) => {
    const productUrl = new URL(url || ROOT, window.location.origin);
    productUrl.search = '';
    productUrl.hash = '';
    if (!productUrl.pathname.endsWith('.js')) {
      productUrl.pathname = `${productUrl.pathname.replace(/\/$/, '')}.js`;
    }
    return productUrl.toString();
  };

  const fetchProduct = async (url) => {
    const jsonUrl = productJsonUrl(url);
    if (!cache.has(jsonUrl)) {
      cache.set(
        jsonUrl,
        fetch(jsonUrl, { headers: { Accept: 'application/json' } }).then((response) => {
          if (!response.ok) throw new Error(window.zaT("Unable to load product options"));
          return response.json();
        }),
      );
    }
    return cache.get(jsonUrl);
  };

  const optionNamesFor = (product) =>
    (product.options || []).map((option, index) =>
      typeof option === 'string' ? option : option?.name || `Option ${index + 1}`,
    );

  const variantValues = (variant, count) => {
    if (Array.isArray(variant.options) && variant.options.length) return variant.options.map(String);
    return Array.from({ length: count }, (_, index) => String(variant[`option${index + 1}`] || ''));
  };

  const variantImageUrl = (variant) => {
    const image = variant?.featured_image || variant?.featured_media?.preview_image || variant?.image;
    if (typeof image === 'string') return normalizeImage(image);
    return normalizeImage(image?.src || image?.url || '');
  };

  const setProductImage = (image, primary, fallback, alt) => {
    const source = normalizeImage(primary) || normalizeImage(fallback);
    const fallbackSource = normalizeImage(fallback);
    image.alt = alt || '';
    image.onerror = () => {
      if (fallbackSource && image.src !== fallbackSource) {
        image.onerror = () => image.removeAttribute('src');
        image.src = fallbackSource;
      } else {
        image.removeAttribute('src');
      }
    };
    image.src = source || BLANK_IMAGE;
  };

  const matchingVariant = (variants, optionNames, selections) =>
    variants.find((variant) => {
      if (variant.available === false) return false;
      const values = variantValues(variant, optionNames.length);
      return values.every((value, index) => value === selections[index]);
    }) || null;

  const partialImageVariant = (variants, optionNames, selections) =>
    variants.find((variant) => {
      if (variant.available === false || !variantImageUrl(variant)) return false;
      const values = variantValues(variant, optionNames.length);
      return selections.every((selected, index) => !selected || values[index] === selected);
    }) || null;

  const ensurePicker = () => {
    let picker = document.querySelector('[data-za-quick-add-picker]');
    if (picker) return picker;

    picker = create('div', 'za-quick-add');
    picker.hidden = true;
    picker.dataset.zaQuickAddPicker = '';
    picker.innerHTML = `
      <div class="za-quick-add__dialog" role="dialog" aria-modal="true" aria-labelledby="ZaQuickAddTitle">
        <button class="za-quick-add__close" type="button" data-za-quick-add-close aria-label="${window.zaT("Close option selector")}">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"></path></svg>
        </button>
        <div class="za-quick-add__head">
          <img class="za-quick-add__image" src="${BLANK_IMAGE}" data-za-quick-add-image alt="">
          <div class="za-quick-add__copy">
            <p>${window.zaT("Choose your options")}</p>
            <h2 id="ZaQuickAddTitle" data-za-quick-add-title></h2>
          </div>
        </div>
        <div class="za-quick-add__options" data-za-quick-add-options></div>
        <p class="za-quick-add__status" data-za-quick-add-status>${window.zaT("Please select all options.")}</p>
        <button class="za-quick-add__confirm" type="button" data-za-quick-add-confirm disabled>${window.zaT("Select options")}</button>
      </div>
    `;
    document.body.append(picker);
    return picker;
  };

  const closePicker = () => {
    const picker = document.querySelector('[data-za-quick-add-picker]');
    if (picker) picker.hidden = true;
    pickerState = null;
    document.body.classList.remove('za-quick-add-open');
    const restore = pickerReturn; pickerReturn = null; restore?.();
  };

  const renderPickerState = () => {
    if (!pickerState) return;

    const picker = ensurePicker();
    const { product, optionNames, selections } = pickerState;
    const variants = Array.isArray(product.variants) ? product.variants : [];

    picker.querySelectorAll('[data-za-quick-add-option-index]').forEach((button) => {
      const index = Number(button.dataset.zaQuickAddOptionIndex);
      const value = button.dataset.zaQuickAddOptionValue;
      const possible = variants.some((variant) => {
        if (variant.available === false) return false;
        const values = variantValues(variant, optionNames.length);
        if (values[index] !== value) return false;
        return selections.every((selected, otherIndex) => otherIndex === index || !selected || values[otherIndex] === selected);
      });

      button.disabled = !possible;
      button.classList.toggle('is-selected', selections[index] === value);
      button.setAttribute('aria-pressed', selections[index] === value ? 'true' : 'false');
    });

    const complete = selections.every(Boolean);
    const selectedVariant = complete ? matchingVariant(variants, optionNames, selections) : null;
    pickerState.selectedVariant = selectedVariant;

    const selectedImage = variantImageUrl(selectedVariant) || variantImageUrl(partialImageVariant(variants, optionNames, selections));
    const image = picker.querySelector('[data-za-quick-add-image]');
    const imageUrl = selectedImage || pickerState.defaultImage || normalizeImage(product.featured_image);
    setProductImage(
      image,
      imageUrl,
      normalizeImage(product.featured_image),
      [product.title, selections.find(Boolean)].filter(Boolean).join(' - '),
    );

    const status = picker.querySelector('[data-za-quick-add-status]');
    const confirm = picker.querySelector('[data-za-quick-add-confirm]');

    if (!complete) {
      const missing = optionNames.filter((_, index) => !selections[index]).join(' and ');
      status.textContent = `Please select ${missing}.`;
      confirm.textContent = window.zaT("Select options");
      confirm.disabled = true;
    } else if (!selectedVariant) {
      status.textContent = window.zaT("This combination is currently unavailable.");
      confirm.textContent = 'Unavailable';
      confirm.disabled = true;
    } else {
      status.textContent = window.zaT("Your selected combination is available.");
      confirm.textContent = window.zaT("Add selected item to bag");
      confirm.disabled = false;
    }
    window.zaStylePicker?.(picker,pickerState,'za-quick-add');
  };

  const addVariantToCart = async (variantId, button, quantity = 1) => {
    if (!variantId) throw new Error(window.zaT("A product option must be selected"));

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = window.zaT("Adding...");

    const response = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Math.max(1,Math.min(99,Math.floor(Number(quantity)||1))) }] }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.description || window.zaT("Unable to add item"));

    button.textContent = window.zaT("Added to bag");
    document.dispatchEvent(new CustomEvent('zoalin:quick-add:added', { detail: { variantId, payload } }));

    window.setTimeout(() => {
      const hadPicker = Boolean(pickerState);
      if (hadPicker) closePicker();
      document.dispatchEvent(new CustomEvent('zoalin:cart:added-quiet'));
      if (!hadPicker) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }, 260);
  };

  const openPicker = async (button) => {
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = window.zaT("Loading options...");

    try {
      const product = await fetchProduct(button.dataset.zaProductUrl);
      const optionNames = optionNamesFor(product);
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const availableVariants = variants.filter((variant) => variant.available !== false);

      if (!optionNames.length) throw new Error(window.zaT("No selectable options found"));
      if (variants.length === 1 && availableVariants.length === 1) {
        await addVariantToCart(availableVariants[0].id, button);
        return;
      }

      const picker = ensurePicker();
      const title = button.dataset.zaProductTitle || button.dataset.zaProductCardTitle || product.title || window.zaT("Product");
      const cardImage = normalizeImage(button.dataset.zaProductImage);
      const defaultImage = cardImage || normalizeImage(product.featured_image) || BLANK_IMAGE;
      const firstVariant = availableVariants[0] || variants[0] || null;

      picker.querySelector('[data-za-quick-add-title]').textContent = window.zaT(title);
      const image = picker.querySelector('[data-za-quick-add-image]');
      setProductImage(image, defaultImage, normalizeImage(product.featured_image), title);

      const optionsRoot = picker.querySelector('[data-za-quick-add-options]');
      optionsRoot.replaceChildren();
      optionNames.forEach((name, index) => {
        const group = create('fieldset', 'za-quick-add__group');
        const legend = create('legend', 'za-quick-add__legend', window.zaT(name));
        const valuesRoot = create('div', 'za-quick-add__values');
        const values = [
          ...new Set(variants.map((variant) => variantValues(variant, optionNames.length)[index]).filter(Boolean)),
        ];

        values.forEach((value) => {
          const option = create('button', 'za-quick-add__option', window.zaProductOptionText(value));
          option.type = 'button';
          option.dataset.zaQuickAddOptionIndex = String(index);
          option.dataset.zaQuickAddOptionValue = value;
          option.setAttribute('aria-pressed', 'false');
          valuesRoot.append(option);
        });

        group.append(legend, valuesRoot);
        optionsRoot.append(group);
      });

      pickerState = {
        product,
        optionNames,
        selections: firstVariant ? variantValues(firstVariant, optionNames.length) : Array(optionNames.length).fill(null),
        selectedVariant: null,
        defaultImage,
      };

      picker.hidden = false;
      document.body.classList.add('za-quick-add-open');
      renderPickerState();
      picker.querySelector('[data-za-quick-add-close]')?.focus();
    } catch (_) {
      button.textContent = window.zaT("Choose on product page");
      window.setTimeout(() => {
        window.location.href = button.dataset.zaProductUrl || ROOT;
      }, 350);
    } finally {
      if (pickerState) {
        button.disabled = false;
        button.textContent = originalText;
      } else if (button.textContent === 'Loading options...') {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  };

  window.zaOpenProductPicker = async (button, onClose) => {
    await openPicker(button);
    if (!pickerState) return false;
    pickerReturn = onClose;
    return true;
  };

  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;

    const openButton = event.target.closest('[data-za-quick-add-open]');
    if (openButton) {
      event.preventDefault();
      await openPicker(openButton);
      return;
    }

    const optionButton = event.target.closest('[data-za-quick-add-option-index]');
    if (optionButton && pickerState) {
      const index = Number(optionButton.dataset.zaQuickAddOptionIndex);
      pickerState.selections[index] = optionButton.dataset.zaQuickAddOptionValue;
      renderPickerState();
      return;
    }

    const confirm = event.target.closest('[data-za-quick-add-confirm]');
    if (confirm && pickerState?.selectedVariant) {
      try {
        await addVariantToCart(pickerState.selectedVariant.id, confirm, pickerState.quantity);
      } catch (_) {
        confirm.disabled = false;
        confirm.textContent = window.zaT("Try again");
      }
      return;
    }

    if (event.target.closest('[data-za-quick-add-close]') || event.target.matches('[data-za-quick-add-picker]')) {
      closePicker();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && pickerState) closePicker();
  });

  document.addEventListener('shopify:section:unload', closePicker);
})();

